import { useInterval } from '@mantine/hooks'
import type { Duration } from 'dayjs/plugin/duration'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useLatest from '@/hooks/useLatest'

import dayjs from '../utils/dayjs'

interface UseCountdownTimerOptions {
  targetTime: Date | number | string
  interval?: number | ((remainingMs: number) => number)
  onEnd?: () => void
  enabled?: boolean | ((duration: Duration) => boolean)
}

interface CountdownTime {
  days: number
  hours: number
  minutes: number
  seconds: number
  duration: Duration
  isExpired: boolean
  isEnabled: boolean
}

const ZERO_COUNTDOWN_TIME: CountdownTime = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  duration: dayjs.duration(0),
  isExpired: false,
  isEnabled: false,
}

/**
 * Port of cdc-web `src/hooks/useCountdownTimer.ts`（1:1）。dayjs 用本区
 * `utils/dayjs`（已装 duration 插件）；useLatest 用本仓 `src/hooks/useLatest.ts`。
 */
export default function useCountdownTimer(options: UseCountdownTimerOptions): CountdownTime {
  const { targetTime, interval = 1000, onEnd, enabled = true } = options

  // useLatest 避免 enabled/onEnd/targetTime 变动时重跑 effect
  const enabledRef = useLatest(enabled)
  const onEndRef = useLatest(onEnd)
  const targetTimeRef = useLatest(targetTime)

  const calculateTime = (): Omit<CountdownTime, 'isEnabled'> => {
    const target = dayjs(targetTimeRef.current)
    const now = dayjs()
    const diff = target.diff(now)

    if (diff <= 0) {
      return {
        ...ZERO_COUNTDOWN_TIME,
        isExpired: true,
      }
    }

    const duration = dayjs.duration(diff)

    return {
      days: Math.floor(duration.asDays()),
      hours: duration.hours(),
      minutes: duration.minutes(),
      seconds: duration.seconds(),
      duration,
      isExpired: false,
    }
  }

  const [time, setTime] = useState<CountdownTime>(() => {
    const currentTime = calculateTime()
    const isCurrentlyEnabled =
      typeof enabledRef.current === 'function'
        ? enabledRef.current(currentTime.duration)
        : enabledRef.current
    return { ...currentTime, isEnabled: !!isCurrentlyEnabled }
  })

  const intervalValue = useMemo(() => {
    if (typeof interval === 'function') {
      const diff = dayjs(targetTime).diff(dayjs())
      return interval(Math.max(0, diff))
    }
    return interval
  }, [targetTime, interval])

  // tick 回调必须引用稳定。Mantine `useInterval` 在 fn 变化时重跑 effect
  // （清旧 + 建新 setInterval）；内联回调每渲染一个新引用，若父组件比 interval
  // 更快重渲染，timer 会在触发前被反复重置。tick 只经 refs 读渲染域值，
  // `[]` deps 是安全的。
  const tick = useCallback(() => {
    const newTime = calculateTime()

    if (newTime.isExpired) {
      setTime({ ...newTime, isEnabled: true })
      stopRef.current?.()
      onEndRef.current?.()
      return
    }

    const isCurrentlyEnabled =
      typeof enabledRef.current === 'function'
        ? enabledRef.current(newTime.duration)
        : enabledRef.current

    if (!isCurrentlyEnabled) {
      setTime({ ...newTime, isEnabled: false })
      stopRef.current?.()
      return
    }

    setTime({ ...newTime, isEnabled: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只读 refs；必须保持稳定引用避免 useInterval 重置
  }, [])

  const { start, stop } = useInterval(tick, intervalValue)
  const stopRef = useLatest(stop)

  useEffect(() => {
    const currentTime = calculateTime()

    const isCurrentlyEnabled =
      typeof enabledRef.current === 'function'
        ? enabledRef.current(currentTime.duration)
        : enabledRef.current

    if (!isCurrentlyEnabled) {
      setTime({ ...currentTime, isEnabled: false })
      stop()
      return
    }

    setTime({ ...currentTime, isEnabled: true })
    start()
    return () => stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetTime])

  return time
}
